/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import * as React from "react";
// import { Header, IndeterminateProgressBar } from "@emt/emt-react";
import { LocalizationInfo } from "@emt/emt-localization/lib/localization-info";
import { FilteredValue } from "../components/filter-picker/filter-picker";
import { LocalizationContext } from "../context/LocalizationContext";
import { AIContext } from "../context/AIContext";
import { Environment } from "../utilities/environment";
import {
  EnrollmentDetail,
  InvitationDetail,
} from "../containers/base-container";
import { EnrollmentAgreementPanel } from "../components/modal/enrollment-agreement-panel";
import { EnrollmentBankAndTaxPanel } from "../components/modal/enrollment-bank-and-tax-panel";
import { EnrollmentBankAndTaxWithDefaultPanel } from "../components/modal/enrollment-bank-and-tax-with-default-panel";
import { PermissionNotification } from "../components/permission-notification/permission-notification";
import {
  PaymentAndTaxStatus,
  Enrollment,
  NewEnrollmentRequest,
  AcceptInvitationResponse,
  ProfileAction,
  EnrollmentState,
  DefaultPaymentProfileAssignmentDetails,
} from "../models/model";
import { EnrollmentClient } from "../clients/enrollment-client";
import { RemoveInvitationPayload } from "../redux/actions/actions";
import { WoboBanner } from "@emt/emt-wobo-banner";
import { AlertWarning } from "../components/alert/alert-warning";
import { HybridDeprecationModal } from "../components/modal/hybrid-deprecation-modal";
import { EnrollmentsGrid } from "../components/enrollments-grid/enrollments-grid";
import button from "@harmony/enablers/components/button/button";
import { createScope } from "@harmony/enablers/react/scope-for-react";
import partnerCenterTheme from "@harmony/enablers/themes/partner-center";
import messageBar from "@harmony/enablers/components/message-bar/message-bar";
import pageTitle from "@harmony/enablers/components/page-title/page-title";

export interface EnrollmentPageDispatch {
  addEnrollment: (enrollment: Enrollment) => void;
  removeInvite: (invite: RemoveInvitationPayload) => void;
  updateBankAndTaxStatus: (bankAndTaxStatus: PaymentAndTaxStatus) => void;
}

export class EnrollmentPageProperties {
  fetchedData: boolean = false;
  canShowTable: boolean = false;
  error: boolean = false;
  hybridAccess: boolean = false;
  enrollments: EnrollmentDetail[] = [];
  invitations: InvitationDetail[] = [];
  canShowWobo: boolean = false;
}

class EnrollmentPageState {
  programNameFilter: string = "";
  locationFilter: string = "";
  showEnrollmentAgreementModal: boolean = false;
  showEnrollmentBankAndTaxModal: boolean = false;
  showEnrollmentBankAndTaxWithDefaultPanel: boolean = false;
  showHybridDeprecationModal: boolean = true;
  selectedInvitationDetail: EnrollmentDetail;
  startEnrollmentApiError: boolean = false;
  isLoaded: boolean = false;
  defaultPaymentProfileDetails: DefaultPaymentProfileAssignmentDetails =
    {} as DefaultPaymentProfileAssignmentDetails;
  isLoadingBankAndTaxPanel: boolean = false;
  showSuccessfullyUsedDefaults: boolean = false;
  showSubmitWithDefaultsErrorBanner: boolean = false;
  isSubmitWithDefaultsLoading: boolean = false;
  isBannerExpanded: boolean = false;
  bannerLiveRegionMsg: string = "";
}

const scope = createScope({
  reactInstance: React,
  styles: [partnerCenterTheme],
});

const LocationCode: string = "location";
const ProgramNameCode: string = "programName";
const enrollmentClient: EnrollmentClient = new EnrollmentClient();
const Button = scope.forReact(button);
const PageTitle = scope.forReact(pageTitle);
const MessageBar = scope.forReact(messageBar);

export type EnrollmentPageProps = EnrollmentPageDispatch &
  EnrollmentPageProperties;

export class EnrollmentsPage extends React.Component<
  EnrollmentPageProps,
  EnrollmentPageState,
  EnrollmentClient
> {
  private messageBarRef = React.createRef<HTMLDivElement>();
  private cleanupClickHandler: (() => void) | null = null;

  constructor(props: EnrollmentPageProps) {
    super(props);
    this.state = new EnrollmentPageState();
  }

  handleStartEnrollmentApiError(selectedRow: EnrollmentDetail) {
    this.setState({
      ...this.state,
      showEnrollmentAgreementModal: true,
      showEnrollmentBankAndTaxModal: false,
      showEnrollmentBankAndTaxWithDefaultPanel: false,
      selectedInvitationDetail: selectedRow,
      startEnrollmentApiError: true,
    });
  }

  handleEnrollNow(selectedRow: EnrollmentDetail) {
    this.setState({
      ...this.state,
      showEnrollmentAgreementModal: true,
      showEnrollmentBankAndTaxModal: false,
      showEnrollmentBankAndTaxWithDefaultPanel: false,
      startEnrollmentApiError: false,
      selectedInvitationDetail: selectedRow,
      showSuccessfullyUsedDefaults: false,
      showSubmitWithDefaultsErrorBanner: false,
    });
  }

  handleContinueClickOnAgreementModal(
    invitationDetail: EnrollmentDetail,
    appInsights: any
  ) {
    // build a request object
    const enrollmentRequest: NewEnrollmentRequest[] = [
      {
        partnerId: invitationDetail.partnerId,
        offeringId: invitationDetail.offeringId,
      },
    ];

    this.setState({
      ...this.state,
      isLoadingBankAndTaxPanel: true,
    });

    // call StartEnrollment Api
    enrollmentClient
      .startNewEnrollment(enrollmentRequest)
      .then((acceptInvitationResponse: AcceptInvitationResponse[]) => {
        const response = acceptInvitationResponse[0];
        const enrollment: Enrollment = {
          countryName: invitationDetail.countryName,
          enrollmentState: EnrollmentState.Unknown,
          partnerLocation: invitationDetail.locationName,
          partnerId: invitationDetail.partnerId,
          offeringId: invitationDetail.offeringId,
          offeringGuid: invitationDetail.offeringGuid,
          isActive: false,
          accessViaLegacy: false,
        };

        enrollmentClient
          .getDefaultPaymentProfiles(
            invitationDetail.partnerId.value,
            invitationDetail.offeringGuid
          )
          .then(
            (
              defaultPaymentProfileAssignmentDetails: DefaultPaymentProfileAssignmentDetails
            ) => {
              this.setState({
                ...this.state,
                defaultPaymentProfileDetails:
                  defaultPaymentProfileAssignmentDetails,
              });

              if (response && response.state == EnrollmentState.Enrolling) {
                if (
                  this.state.defaultPaymentProfileDetails.selection != null &&
                  this.state.defaultPaymentProfileDetails.selection !=
                    undefined &&
                  this.state.defaultPaymentProfileDetails
                    .enableDefaultPaymentAndTax
                ) {
                  this.setState({
                    ...this.state,
                    // on successful response, go to next modal
                    showEnrollmentAgreementModal: false,
                    showEnrollmentBankAndTaxWithDefaultPanel: true,
                  });
                } else {
                  this.setState({
                    ...this.state,
                    // on successful response, go to next modal
                    showEnrollmentAgreementModal: false,
                    showEnrollmentBankAndTaxModal: true,
                  });
                }
                enrollment.enrollmentState = EnrollmentState.Enrolling;
                enrollment.isActive = true;
                this.props.removeInvite({
                  partnerId: enrollment.partnerId,
                  offeringId: enrollment.offeringId,
                });

                // build a BankAndTaxStatus object to reflect enrolled status
                const bankAndTaxStatus: PaymentAndTaxStatus = {
                  paymentInstrumentAction: ProfileAction.PendingPartner,
                  taxInstrumentAction: ProfileAction.PendingPartner,
                  offeringId: invitationDetail.offeringId,
                  partnerId: invitationDetail.partnerId,
                  taxExpired: false,
                  taxIsExpiring: false,
                };
                // update the bank and tax status
                this.props.updateBankAndTaxStatus(bankAndTaxStatus);
                this.props.addEnrollment(enrollment);
              } else if (
                response &&
                response.state === EnrollmentState.Enrolled
              ) {
                this.setState({
                  ...this.state,

                  // very rare case, where a partner would go to enrolled state directly.
                  // we wouldn't show bank & tax modal in this case,
                  // instead we would show enrolled states.
                  showEnrollmentAgreementModal: false,
                  showEnrollmentBankAndTaxModal: false,
                  showEnrollmentBankAndTaxWithDefaultPanel: false,
                });

                //update the table
                enrollment.enrollmentState = EnrollmentState.Enrolling;
                enrollment.isActive = true;
                this.props.removeInvite({
                  partnerId: enrollment.partnerId,
                  offeringId: enrollment.offeringId,
                });
                this.props.addEnrollment(enrollment);

                // build a BankAndTaxStatus object to reflect enrolled status
                const bankAndTaxStatus: PaymentAndTaxStatus = {
                  paymentInstrumentAction: ProfileAction.NotRequired,
                  taxInstrumentAction: ProfileAction.NotRequired,
                  offeringId: invitationDetail.offeringId,
                  partnerId: invitationDetail.partnerId,
                  taxExpired: false,
                  taxIsExpiring: false,
                };
                // update the bank and tax status
                this.props.updateBankAndTaxStatus(bankAndTaxStatus);
              }

              this.setState({
                ...this.state,
                isLoadingBankAndTaxPanel: false,
              });
            }
          )
          .catch(() => {
            appInsights.trackEvent("Unable to get default payment");
            this.setState({
              ...this.state,
              // on failed response
              showEnrollmentBankAndTaxModal: false,
              showEnrollmentBankAndTaxWithDefaultPanel: false,
              startEnrollmentApiError: true,
              isLoadingBankAndTaxPanel: false,
            });
          });
      })
      .catch(() => {
        this.setState({
          ...this.state,
          // on failed response
          showEnrollmentBankAndTaxModal: false,
          showEnrollmentBankAndTaxWithDefaultPanel: false,
          startEnrollmentApiError: true,
          isLoadingBankAndTaxPanel: false,
        });
      });
  }

  handleCloseAgreementModal() {
    this.setState({
      ...this.state,
      showEnrollmentAgreementModal: false,
      startEnrollmentApiError: false,
    });
  }

  handleCloseBankAndTaxPanel() {
    this.setState({
      ...this.state,
      showEnrollmentBankAndTaxModal: false,
    });
  }

  handleCloseBankAndTaxWithDefaultPanel() {
    this.setState({
      ...this.state,
      showEnrollmentBankAndTaxWithDefaultPanel: false,
    });
  }

  handleCloseHybridDecprecationModal() {
    this.setState({
      ...this.state,
      showHybridDeprecationModal: false,
    });
  }

  handleContinueBankAndTaxPanel() {
    this.setState({
      ...this.state,
      showEnrollmentBankAndTaxModal: false,
    });
  }

  toggleBannerExpansion() {
    this.setState({
      ...this.state,
      isBannerExpanded: !this.state.isBannerExpanded,
    });
  }

  componentDidMount() {
    this.patchMessageBarButton();
  }

  componentDidUpdate() {
    this.patchMessageBarButton();
  }

  /**
   * The Harmony MessageBar sets aria-expanded on the inner native <button> inside
   * its shadow DOM, but NVDA focuses the he-button host element and cannot read it.
   * This patch sets aria-expanded directly on the he-button host so NVDA correctly
   * announces the expanded/collapsed state. A live region announces the state change
   * after the button is clicked.
   */
  private patchMessageBarButton = () => {
    requestAnimationFrame(() => {
      const container = this.messageBarRef.current;
      if (!container) return;
      const el = container.querySelector('he-message-bar') as any;
      if (!el?.shadowRoot) return;
      const btn = el.shadowRoot.querySelector('.message-bar__multiline__button') as HTMLElement;
      if (!btn) return;

      el.setAttribute('aria-expanded', String(!!el.expanded));

      if (!(el as any)._ariaPatched) {
        (el as any)._ariaPatched = true;
        const clickHandler = () => {
          requestAnimationFrame(() => {
            const expanded = !!el.expanded;
            el.setAttribute('aria-expanded', String(expanded));
            const msg = `More info button ${expanded ? 'expanded' : 'collapsed'}`;
            this.setState({ bannerLiveRegionMsg: '' }, () => {
              this.setState({ bannerLiveRegionMsg: msg });
            });
          });
        };
        btn.addEventListener('click', clickHandler);
        this.cleanupClickHandler = () => {
          btn.removeEventListener('click', clickHandler);
        };
      }
    });
  };

  componentWillUnmount() {
    if (this.cleanupClickHandler) {
      this.cleanupClickHandler();
    }
  }

  render(): JSX.Element {
    return (
      <LocalizationContext.Consumer>
        {(localization) => (
          <AIContext.Consumer>
            {(appInsights) => this.renderContent(localization, appInsights)}
          </AIContext.Consumer>
        )}
      </LocalizationContext.Consumer>
    );
  }

  renderContent(localization: LocalizationInfo, appInsights: any): JSX.Element {
    var filteredEnrollments = this.filteredEnrollments();
    var isWorkspace = Environment.isWorkspace();

    if (appInsights.trackEvent && !this.state.isLoaded) {
      appInsights.trackEvent({ name: "EnrollmentPageLoaded" });
      this.setState({ isLoaded: true });
    }

    return (
      <>
        <div
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'absolute',
            left: '-10000px',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          {this.state.bannerLiveRegionMsg}
        </div>
        {this.props.canShowWobo && (
          <WoboBanner
            onClearWoboSession={() =>
              (window.location.href =
                "/dashboard/incentives/enrollment/summary")
            }
          />
        )}

        {!Environment.isModern() &&
          localization.strings["MsaHybridDeprecationBannerContent"] != null && (
            <AlertWarning
              content={
                <p>
                  {
                    localization.strings[
                      "MsaHybridDeprecationBannerContent"
                    ].split("{0}")[0]
                  }
                  <Button
                    external
                    appearance={"link"}
                    target="_blank"
                    href="/en-us/dashboard/incentives/enrollment/web"
                    onClick={() => {
                      appInsights.trackEvent(
                        "ClickedHybridBannerIncentivesSummary"
                      );
                    }}
                  >
                    {
                      localization.strings[
                        "MsaHybridDeprecationBannerContent"
                      ].split("{0}")[1]
                    }
                  </Button>
                  {
                    localization.strings[
                      "MsaHybridDeprecationBannerContent"
                    ].split("{0}")[2]
                  }
                  <Button
                    external
                    appearance={"link"}
                    target="_blank"
                    href="/en-us/pcv/users"
                    onClick={() => {
                      appInsights.trackEvent(
                        "ClickedHybridBannerUserManagement"
                      );
                    }}
                  >
                    {
                      localization.strings[
                        "MsaHybridDeprecationBannerContent"
                      ].split("{0}")[3]
                    }
                  </Button>
                  {
                    localization.strings[
                      "MsaHybridDeprecationBannerContent"
                    ].split("{0}")[4]
                  }
                </p>
              }
            />
          )}

        {Environment.isModern() &&
          this.props.hybridAccess &&
          localization.strings["AadHybridDeprecationBannerContent"] != null && (
            <AlertWarning
              content={
                <p>
                  {
                    localization.strings["AadHybridDeprecationBannerContent"]
                      .split("{0}")[0]
                      .split("{1}")[0]
                  }
                  <span className="win-icon win-icon-marble-sm status-warning"></span>
                  {
                    localization.strings["AadHybridDeprecationBannerContent"]
                      .split("{0}")[0]
                      .split("{1}")[1]
                  }
                  <Button
                    external
                    appearance={"link"}
                    target="_blank"
                    href="/en-us/dashboard/incentives/enrollment/summary"
                    onClick={() => {
                      appInsights.trackEvent(
                        "ClickedHybridBannerIncentivesSummary"
                      );
                    }}
                  >
                    {
                      localization.strings[
                        "AadHybridDeprecationBannerContent"
                      ].split("{0}")[1]
                    }
                  </Button>
                  {
                    localization.strings[
                      "AadHybridDeprecationBannerContent"
                    ].split("{0}")[2]
                  }
                  <Button
                    external
                    appearance={"link"}
                    target="_blank"
                    href="/en-us/pcv/users"
                    onClick={() => {
                      appInsights.trackEvent(
                        "ClickedHybridBannerUserManagement"
                      );
                    }}
                  >
                    {
                      localization.strings[
                        "AadHybridDeprecationBannerContent"
                      ].split("{0}")[3]
                    }
                  </Button>
                  {
                    localization.strings[
                      "AadHybridDeprecationBannerContent"
                    ].split("{0}")[4]
                  }
                </p>
              }
            />
          )}

        <div className="enrollment-page" style={{ overflowX: "hidden" }}>
          <div className="row">
            <div className="col-xs-24 col-sm-16 spacer-xs-top spacer-ml-bottom">
              {isWorkspace ? (
                <PageTitle>
                  {localization.strings["IncentivesTitle"]}
                  <span slot="active-title">
                    {localization.strings["OverviewTitle"]}
                  </span>
                </PageTitle>
              ) : (
                <PageTitle>{localization.strings["OverviewHeader"]}</PageTitle>
              )}
            </div>
            {localization.strings["TableWidgetRemovalBannerTitle"] != null &&
              localization.strings["TableWidgetRemovalBannerPointOne"] !=
                null &&
              localization.strings["TableWidgetRemovalBannerPointTwo"] !=
                null && (
                <div className="col-xs-24 spacer-md-bottom">
                  <div ref={this.messageBarRef}>
                  <MessageBar appearance="notice" open expandable>
                    <div>
                      {
                        localization.strings[
                          "TableWidgetRemovalBannerText"
                        ].split("{0}")[0]
                      }
                      <Button
                        style={{
                          paddingBottom: 6,
                        }}
                        appearance={"link"}
                        target={"_blank"}
                        external
                        href="https://learn.microsoft.com/en-us/partner-center/announcements/2023-september"
                      >
                        <span style={{ fontSize: 12 }}>
                          {
                            localization.strings[
                              "TableWidgetRemovalBannerText"
                            ].split("{0}")[1]
                          }
                        </span>
                      </Button>
                      {
                        localization.strings[
                          "TableWidgetRemovalBannerText"
                        ].split("{0}")[2]
                      }
                    </div>
                  </MessageBar>
                  </div>
                </div>
              )}
          </div>

          <PermissionNotification localization={localization}>
            <div className="row">
              <div className="col-xs-12">
                <div className="text-body spacer-md-bottom">
                  {localization.strings["TableExplainationText"] + " "}
                  <Button
                    external
                    appearance={"link"}
                    target="_blank"
                    href="https://go.microsoft.com/fwlink/?linkid=2116501"
                    style={{ paddingBottom: 4.5, lineHeight: 2 }}
                    onClick={() => {
                      appInsights.trackEvent("ClickedSupportDocLink");
                    }}
                  >
                    {" "}
                    {localization.strings["TableLearnMoreText"] + " "}
                  </Button>
                </div>
                <div className="text-body spacer-md-bottom">
                  {localization.strings["TableExplanationTextContinued"] && (
                    <>
                      {
                        localization.strings[
                          "TableExplanationTextContinued"
                        ].split("{0}")[0]
                      }
                      <Button
                        external
                        appearance={"link"}
                        target="_blank"
                        href="/en-us/pcv/users"
                        style={{ paddingBottom: 4.5, lineHeight: 2 }}
                        onClick={() => {
                          appInsights.trackEvent("ClickedUserManagementLink");
                        }}
                      >
                        {
                          localization.strings[
                            "TableExplanationTextContinued"
                          ].split("{0}")[1]
                        }
                      </Button>
                      {
                        localization.strings[
                          "TableExplanationTextContinued"
                        ].split("{0}")[2]
                      }
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* {!this.props.fetchedData && <Progress label="Progress" indeterminate />} */}

            {this.props.error && (
              <MessageBar appearance="error" open>
                {localization.strings["ErrorFetchingDataMessage"]}{" "}
                {<a onClick={() => location.reload()}></a>}{" "}
                {localization.strings["TryAgainMessage"]}
              </MessageBar>
            )}

            {this.state.showSuccessfullyUsedDefaults && (
              <MessageBar
                appearance="success"
                open
                onHeAfterHide={(_: any) =>
                  this.setState({ showSuccessfullyUsedDefaults: false })
                }
              >
                {localization.format(
                  "SuccessfulSubmitWithDefaults",
                  this.state.selectedInvitationDetail.programName,
                  this.state.selectedInvitationDetail.partnerId.value
                )}
              </MessageBar>
            )}

            <EnrollmentsGrid
              enrollments={filteredEnrollments}
              invitations={this.filteredInvitations()}
              enrollCallback={(selectedRow: EnrollmentDetail) => {
                this.handleEnrollNow(selectedRow);
                appInsights.trackEvent("StartEnrollment");
              }}
              isLoading={!this.props.fetchedData}
              isEnvironmentModern={Environment.isModern()}
            />

            {this.state.showEnrollmentAgreementModal && (
              <EnrollmentAgreementPanel
                visible={this.state.showEnrollmentAgreementModal}
                invitationDetail={this.state.selectedInvitationDetail}
                startEnrollmentApiError={this.state.startEnrollmentApiError}
                onClose={() => {
                  this.handleCloseAgreementModal();
                  appInsights.trackEvent("CloseAgreementModal");
                }}
                isLoading={true}
                isLoadingBankAndTaxInfo={this.state.isLoadingBankAndTaxPanel}
                onContinue={() => {
                  this.handleContinueClickOnAgreementModal(
                    this.state.selectedInvitationDetail,
                    appInsights
                  );
                  appInsights.trackEvent("CompletedEnrollment");
                }}
              />
            )}

            {this.props.hybridAccess &&
              this.state.showHybridDeprecationModal && (
                <HybridDeprecationModal
                  visible={
                    this.state.showHybridDeprecationModal &&
                    !(Environment.isModern() && !this.props.hybridAccess)
                  }
                  isModern={Environment.isModern()}
                  onClose={() => {
                    this.handleCloseHybridDecprecationModal();
                    appInsights.trackEvent("CloseHybridDeprecationModal");
                  }}
                />
              )}

            {
              <EnrollmentBankAndTaxPanel
                visible={this.state.showEnrollmentBankAndTaxModal}
                invitationDetail={this.state.selectedInvitationDetail}
                onClose={() => {
                  this.handleCloseBankAndTaxPanel();
                  appInsights.trackEvent("CloseBankAndTaxPanel");
                }}
                onContinue={() => {
                  this.handleContinueBankAndTaxPanel();
                  appInsights.trackEvent("ContinueBankAndTaxPanel");
                }}
                isLoading={true}
              />
            }

            {
              <EnrollmentBankAndTaxWithDefaultPanel
                visible={this.state.showEnrollmentBankAndTaxWithDefaultPanel}
                invitationDetail={this.state.selectedInvitationDetail}
                defaultPaymentDetails={this.state.defaultPaymentProfileDetails}
                showSubmitErrorBanner={
                  this.state.showSubmitWithDefaultsErrorBanner
                }
                isSubmitLoading={this.state.isSubmitWithDefaultsLoading}
                onClose={() => {
                  this.handleCloseBankAndTaxWithDefaultPanel();
                  appInsights.trackEvent("CloseBankAndTaxWithDefaultPanel");
                }}
                onDefaultNotChosen={() => {
                  this.handleCloseBankAndTaxWithDefaultPanel();
                  appInsights.trackEvent(
                    "UserDidNotChooseDefault-CloseBankAndTaxWithDefaultPanel"
                  );
                }}
                onSubmit={() => {
                  this.setState({ isSubmitWithDefaultsLoading: true });
                  enrollmentClient
                    .applyDefaultPaymentProfileSettings(
                      this.state.selectedInvitationDetail.partnerId.value,
                      this.state.selectedInvitationDetail.offeringGuid
                    )
                    .then(() =>
                      enrollmentClient
                        .getBankAndTaxStatusForPartnerAndProgram(
                          this.state.selectedInvitationDetail.partnerId.value,
                          this.state.selectedInvitationDetail.offeringGuid
                        )
                        .then((result) => {
                          if (result) {
                            this.props.updateBankAndTaxStatus(result);
                          } else {
                            appInsights.trackEvent(
                              "FailedUpdateBankTaxStatusAfterDefaultSubmission"
                            );
                          }

                          this.handleCloseBankAndTaxWithDefaultPanel();
                          this.setState({
                            showSuccessfullyUsedDefaults: true,
                            isSubmitWithDefaultsLoading: false,
                          });
                        })
                        .catch(() => {
                          this.handleCloseBankAndTaxWithDefaultPanel();
                          this.setState({
                            showSuccessfullyUsedDefaults: true,
                            isSubmitWithDefaultsLoading: false,
                          });
                        })
                    )
                    .catch(() => {
                      this.setState({
                        showSubmitWithDefaultsErrorBanner: true,
                        isSubmitWithDefaultsLoading: false,
                      });
                      appInsights.trackEvent(
                        "Unable to apply default payment and tax"
                      );
                    });
                  appInsights.trackEvent("SubmitBankAndTaxWithDefaultPanel");
                }}
                onRedirect={() => {
                  this.handleCloseBankAndTaxWithDefaultPanel();
                  appInsights.trackEvent("ContinueBankAndTaxWithDefaultPanel");
                }}
                isLoading={true}
              />
            }
          </PermissionNotification>
        </div>
      </>
    );
  }

  private filteredEnrollments(): EnrollmentDetail[] {
    return this.props.enrollments.filter(
      (c) =>
        (this.state.programNameFilter === "" ||
          c.programName === this.state.programNameFilter) &&
        (this.state.locationFilter === "" ||
          c.countryName === this.state.locationFilter)
    );
  }

  private filteredInvitations(): InvitationDetail[] {
    return this.props.invitations.filter(
      (c) =>
        (this.state.programNameFilter === "" ||
          c.programName === this.state.programNameFilter) &&
        (this.state.locationFilter === "" ||
          c.countryName === this.state.locationFilter)
    );
  }

  updatedFilters(filteredValues: FilteredValue[]): void {
    this.setState({
      ...this.state,
      programNameFilter: this.getValueForFilter(
        ProgramNameCode,
        filteredValues
      ),
      locationFilter: this.getValueForFilter(LocationCode, filteredValues),
    });
  }

  getValueForFilter(code: string, filteredValues: FilteredValue[]): string {
    const values = filteredValues.filter((f) => f.code === code);
    if (values.length === 0) {
      return "";
    }
    return values[0].value;
  }

  locIsLoading(localization: LocalizationInfo, key: string): boolean {
    if (typeof localization.strings[key] == "undefined") {
      return true;
    }
    return false;
  }
}